import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { JsonPanel } from "../components/JsonPanel";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import MemoryIcon from "@mui/icons-material/Memory";
import TimerIcon from "@mui/icons-material/Timer";
import {
  Button,
  DatagridConfigurable,
  DateField,
  FilterButton,
  FunctionField,
  List,
  ListButton,
  NumberField,
  Pagination,
  SearchInput,
  SelectColumnsButton,
  SelectInput,
  Show,
  ShowButton,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useInput,
  useListContext,
  WrapperField,
} from "react-admin";

type Assessment = {
  level?: string;
};

type AnalysisScores = {
  total_score?: number | null;
  score?: { assessment?: Assessment | null } | null;
  gum_health?: { score?: number | null; assessment?: Assessment | null } | null;
  plaque?: { coverage_percent?: number | null; assessment?: Assessment | null } | null;
};

type AnalysisSession = {
  id: string;
  userEmail?: string | null;
  deviceUuid?: string | null;
  deviceInfo?: unknown;
  formAnswersList?: unknown;
  formAnswerJson?: unknown;
  createdAt?: string;
};

type AnalysisResult = {
  id: string;
  sessionId: string;
  session: AnalysisSession;
  jobId: string;
  status: string;
  gumHealthLevel?: number | null;
  plaqueLevel?: number | null;
  whitenessScore?: number | null;
  stainScore?: number | null;
  brightnessScore?: number | null;
  whitenessRank?: string;
  scores?: AnalysisScores | null;
  result?: {
    images?: Record<string, string | null>;
    scores?: {
      zyunnbihaku?: { score?: number | null; rank?: string };
      stain?: MetricScores;
      brightness?: MetricScores;
    };
    [key: string]: unknown;
  } | null;
  oralHealthInfo?: unknown;
  originalImages?: Record<string, string | null> | null;
  recommendationLogicName?: string;
  recommendedProductIds?: unknown;
  recommendedAdditionalProductIds?: unknown;
  recommendedProducts?: RecommendedProduct[];
  recommendedAdditionalProducts?: RecommendedProduct[];
  queuedAt?: string;
  processingStartedAt?: string;
  processingCompletedAt?: string;
  algorithmProcessingTimeMs?: number | null;
  workerInstanceType?: string;
  analyzedAt?: string;
  failedAt?: string;
  errorMessage?: string;
  createdAt: string;
};

type MetricScores = {
  score?: number | null;
  score_lower?: number | null;
  score_upper?: number | null;
  coverage_lower?: number | null;
  coverage_upper?: number | null;
};

export const hasMetricData = (scores?: MetricScores, score?: number | null) =>
  [
    scores?.score ?? score,
    scores?.score_lower,
    scores?.score_upper,
    scores?.coverage_lower,
    scores?.coverage_upper,
  ].some((value) => value != null && Number.isFinite(Number(value)));

type RecommendedProduct = {
  id: string;
  name: string;
  title?: string;
  imageUrl?: string;
};

export const hasRecommendedProducts = (products?: RecommendedProduct[], additionalProducts?: RecommendedProduct[]) =>
  Boolean(products?.length || additionalProducts?.length);

const statusLabels: Record<string, string> = {
  queued: "Queued",
  processing: "Processing",
  complete: "Complete",
  failed: "Failed",
};

const statusColors: Record<string, "default" | "info" | "success" | "error"> = {
  queued: "default",
  processing: "info",
  complete: "success",
  failed: "error",
};

const resultColumnsPreferenceKey = "mouthscanAnalysisResults.datagrid.v3";

const DateFilterResetContext = createContext({ resetToken: 0, resetDateInputs: () => {} });

const DateFilterResetProvider = ({ children }: { children: ReactNode }) => {
  const [resetToken, setResetToken] = useState(0);
  return (
    <DateFilterResetContext.Provider value={{ resetToken, resetDateInputs: () => setResetToken((token) => token + 1) }}>
      {children}
    </DateFilterResetContext.Provider>
  );
};

export const formatDateDigits = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
};

export const parseDayMonthYear = (value: string) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return null;
  const [, day, month, year] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year) || date.getUTCMonth() !== Number(month) - 1) return null;
  return `${year}-${month}-${day}`;
};

const isoToDayMonthYear = (value: unknown) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ""));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

const DayMonthYearInput = ({ source, label }: { source: string; label: string; alwaysOn?: boolean }) => {
  const { field } = useInput({ source });
  const { resetToken } = useContext(DateFilterResetContext);
  const [text, setText] = useState(() => isoToDayMonthYear(field.value));
  const isInvalid = text.length === 10 && !parseDayMonthYear(text);

  useEffect(() => {
    if (resetToken) setText("");
  }, [resetToken]);

  useEffect(() => {
    // Keep the text in sync when the filter changes outside the input, e.g. from the URL.
    const nextText = isoToDayMonthYear(field.value);
    setText((currentText) => (nextText ? nextText : parseDayMonthYear(currentText) ? "" : currentText));
  }, [field.value]);

  return (
    <MuiTextField
      id={source}
      name={source}
      label={label}
      value={text}
      placeholder="dd/mm/yyyy"
      variant="filled"
      size="small"
      error={isInvalid}
      helperText={isInvalid ? "Invalid date" : undefined}
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: "numeric", maxLength: 10, "aria-label": label }}
      onChange={(event) => {
        const nextText = formatDateDigits(event.target.value);
        setText(nextText);
        field.onChange(parseDayMonthYear(nextText) ?? "");
      }}
      onBlur={field.onBlur}
    />
  );
};

const resultFilters = [
  <SearchInput key="search" source="search" alwaysOn placeholder="Job ID, session ID, or email" />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: "queued", name: "Queued" },
      { id: "processing", name: "Processing" },
      { id: "complete", name: "Complete" },
      { id: "failed", name: "Failed" },
    ]}
  />,
  <DayMonthYearInput key="createdFrom" source="createdFrom" alwaysOn label="Created from" />,
  <DayMonthYearInput key="createdTo" source="createdTo" alwaysOn label="Created to" />,
];

export const ClearDateFiltersButton = () => {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const { resetDateInputs } = useContext(DateFilterResetContext);

  return (
    <Button
      label="Clear dates"
      onClick={() => {
        resetDateInputs();
        const { createdFrom, createdTo, ...remainingFilters } = filterValues;
        setFilters(remainingFilters, displayedFilters);
      }}
    >
      <EventBusyIcon />
    </Button>
  );
};

const ListActions = () => (
  <TopToolbar>
    <ClearDateFiltersButton />
    <SelectColumnsButton preferenceKey={resultColumnsPreferenceKey} />
    <FilterButton />
  </TopToolbar>
);

const formatScore = (value: number | null | undefined, suffix = "") => {
  if (value == null || !Number.isFinite(Number(value))) return "Not available";
  return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
};

const formatAnalysisDuration = ({ processingStartedAt, processingCompletedAt }: AnalysisResult) => {
  if (!processingStartedAt || !processingCompletedAt) return "Not available";
  const duration = (Date.parse(processingCompletedAt) - Date.parse(processingStartedAt)) / 1000;
  if (!Number.isFinite(duration) || duration < 0) return "Not available";
  const seconds = Math.round(duration);
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

const formatAlgorithmProcessingTime = (record: AnalysisResult) => {
  const value = record.algorithmProcessingTimeMs;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(value / 1000)} s`;
  }
  const fallback = formatAnalysisDuration(record);
  return fallback === "Not available" ? "N/A" : fallback;
};

const formatWorkerInstanceType = ({ workerInstanceType }: AnalysisResult) =>
  workerInstanceType ? workerInstanceType : "N/A";

const IconLabel = ({ title, icon }: { title: string; icon: ReactNode }) => (
  <Tooltip title={title}>
    <Box component="span" aria-label={title} sx={{ display: "inline-flex", verticalAlign: "middle" }}>
      {icon}
    </Box>
  </Tooltip>
);

const algorithmTimeLabel = <IconLabel title="Algorithm time" icon={<TimerIcon fontSize="small" />} />;
const workerInstanceLabel = <IconLabel title="Worker instance" icon={<MemoryIcon fontSize="small" />} />;

const DetailField = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ typography: "body2", overflowWrap: "anywhere" }}>{children ?? "Not available"}</Box>
  </Box>
);

const DetailSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box>
    <Typography
      variant="overline"
      color="text.secondary"
      component="h3"
      sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const MetricDetails = ({ title, scores, score }: { title: string; scores?: MetricScores; score?: number | null }) => (
  <DetailSection title={title}>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 2 }}>
      <DetailField label="Score">{formatScore(scores?.score ?? score)}</DetailField>
      <DetailField label="Lower">{formatScore(scores?.score_lower)}</DetailField>
      <DetailField label="Upper">{formatScore(scores?.score_upper)}</DetailField>
      {title === "Stain" ? (
        <>
          <DetailField label="Coverage lower">
            {formatScore(scores?.coverage_lower == null ? null : scores.coverage_lower * 100, "%")}
          </DetailField>
          <DetailField label="Coverage upper">
            {formatScore(scores?.coverage_upper == null ? null : scores.coverage_upper * 100, "%")}
          </DetailField>
        </>
      ) : null}
    </Box>
  </DetailSection>
);

const RecommendedProducts = ({ products }: { products?: RecommendedProduct[] }) =>
  products?.length ? (
    <Stack spacing={1.5}>
      {products.map((product) => (
        <Box key={product.id}>
          <Link href={`/geoffreyProductRecommendations/${encodeURIComponent(product.id)}`}>
            {product.title || product.name}
          </Link>
          {product.imageUrl ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt={product.name}
              sx={{ display: "block", height: 80, maxWidth: "100%", objectFit: "contain", mt: 1 }}
            />
          ) : null}
        </Box>
      ))}
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      No recommended products
    </Typography>
  );

export const formatAssessmentLevel = (level?: string | null) => {
  const text = level?.trim().replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
};

const AssessmentChip = ({ level }: { level?: string | null }) => {
  const label = formatAssessmentLevel(level);
  return label ? <Chip size="small" label={label} /> : <>Not available</>;
};

const ScoreCard = ({ label, value, assessment }: { label: string; value: string; assessment?: string | null }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={600} sx={{ my: 1 }}>
        {value}
      </Typography>
      {assessment ? <AssessmentChip level={assessment} /> : null}
    </CardContent>
  </Card>
);

const imageModes = ["illust", "mono", "color"] as const;
const gumPlaqueImageRows = [
  [
    "gum_illustration",
    "upper_original",
    "upper_gum_overlay",
    "upper_plaque_overlay",
    "upper_gum_mono_overlay",
    "upper_plaque_mono_overlay",
  ],
  [
    "plaque_illustration",
    "lower_original",
    "lower_gum_overlay",
    "lower_plaque_overlay",
    "lower_gum_mono_overlay",
    "lower_plaque_mono_overlay",
  ],
];
const gumPlaqueImageKeys = new Set(gumPlaqueImageRows.flat());
const imageGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(6, minmax(0, 1fr))",
  },
  gap: 2,
  maxWidth: 1760,
};
type ImageMode = typeof imageModes[number];

export const AnalysisImageViewer = ({ images }: { images?: Record<string, string | null> }) => {
  const [mode, setMode] = useState<ImageMode>("illust");
  const imageFor = (key: string) => images?.[key]?.trim() || null;
  const availableModes = imageModes.filter((item) =>
    item === "illust" ? imageFor("illust_base") : imageFor(`upper_${item}`) || imageFor(`lower_${item}`)
  );
  const selectedMode = availableModes.includes(mode) ? mode : availableModes[0];
  const illustration = imageFor("illust_base");
  const upperImage = imageFor(`upper_${selectedMode}`);
  const lowerImage = imageFor(`lower_${selectedMode}`);
  const gumPlaqueRows = gumPlaqueImageRows
    .map((row) => row.map((key) => [key, imageFor(key)] as const).filter((entry) => entry[1]))
    .filter((row) => row.length);
  const additionalImages = Object.entries(images ?? {}).filter(
    (entry): entry is [string, string] =>
      Boolean(entry[1]?.trim()) &&
      !gumPlaqueImageKeys.has(entry[0]) &&
      !/^(illust_base|(upper|lower)_(mono|color))$/.test(entry[0])
  );

  if (!availableModes.length && !gumPlaqueRows.length && !additionalImages.length) {
    return <Typography color="text.secondary">No result images are available for this analysis.</Typography>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Analysis images
      </Typography>
      {availableModes.length ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={selectedMode}
          onChange={(_event, nextMode: ImageMode | null) => {
            if (nextMode) setMode(nextMode);
          }}
          aria-label="Analysis image display mode"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="illust" disabled={!availableModes.includes("illust")} aria-label="Illustration image">
            Illustration
          </ToggleButton>
          <ToggleButton value="mono" disabled={!availableModes.includes("mono")} aria-label="Monochrome images">
            Mono
          </ToggleButton>
          <ToggleButton value="color" disabled={!availableModes.includes("color")} aria-label="Color images">
            Color
          </ToggleButton>
        </ToggleButtonGroup>
      ) : null}
      {selectedMode === "illust" ? (
        <ResultImage url={illustration} label="Illustration" />
      ) : selectedMode ? (
        <Grid container spacing={2} sx={{ maxWidth: 600 }}>
          <Grid item xs={12} md={6}>
            <ResultImage url={upperImage} label="Upper" />
          </Grid>
          <Grid item xs={12} md={6}>
            <ResultImage url={lowerImage} label="Lower" />
          </Grid>
        </Grid>
      ) : null}
      {gumPlaqueRows.length ? (
        <Stack spacing={2} sx={{ mt: availableModes.length ? 2 : 0 }}>
          {gumPlaqueRows.map((row) => (
            <Box key={row[0][0]} data-testid="gum-plaque-image-row" sx={imageGridSx}>
              {row.map(([key, url]) => (
                <ResultImage key={key} url={url} label={key.replace(/_/g, " ")} />
              ))}
            </Box>
          ))}
        </Stack>
      ) : null}
      {additionalImages.length ? (
        <Box sx={{ ...imageGridSx, mt: availableModes.length || gumPlaqueRows.length ? 2 : 0 }}>
          {additionalImages.map(([key, url]) => (
            <ResultImage key={key} url={url} label={key.replace(/_/g, " ")} />
          ))}
        </Box>
      ) : null}
    </Paper>
  );
};

const ResultImage = ({ url, label }: { url: string | null; label: string }) => (
  <Card variant="outlined" sx={{ width: "100%", maxWidth: 280, height: "100%", overflow: "hidden" }}>
    {url ? (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${label.toLowerCase()} image in a new tab`}
        sx={{ display: "block", bgcolor: "grey.100" }}
      >
        <Box
          component="img"
          src={url}
          alt={label}
          loading="lazy"
          sx={{ display: "block", width: "100%", aspectRatio: "4 / 3", objectFit: "contain" }}
        />
      </Box>
    ) : (
      <Box
        role="img"
        aria-label={`${label} image is not available`}
        sx={{
          display: "grid",
          placeItems: "center",
          aspectRatio: "4 / 3",
          bgcolor: "grey.100",
          color: "text.secondary",
          typography: "body2",
        }}
      >
        {label} image not available
      </Box>
    )}
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="subtitle2">{label}</Typography>
    </CardContent>
  </Card>
);

const AnalysisOverview = ({ record }: { record: AnalysisResult }) => {
  const scores = record.scores;
  const resultScores = record.result?.scores;
  const whitenessRank = record.whitenessRank || resultScores?.zyunnbihaku?.rank;
  const whitenessScore = resultScores?.zyunnbihaku?.score ?? record.whitenessScore;
  const hasWhitenessData =
    Boolean(whitenessRank?.trim()) || (whitenessScore != null && Number.isFinite(Number(whitenessScore)));
  const stainScores = resultScores?.stain;
  const brightnessScores = resultScores?.brightness;
  const hasAvailableStainData = hasMetricData(stainScores, record.stainScore);
  const hasAvailableBrightnessData = hasMetricData(brightnessScores, record.brightnessScore);
  const hasAvailableRecommendedProducts = hasRecommendedProducts(
    record.recommendedProducts,
    record.recommendedAdditionalProducts
  );
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
          <DetailField label="ID">{record.id}</DetailField>
          <DetailField label="Created at">
            <DateField source="createdAt" record={record} showTime emptyText="Not available" />
          </DetailField>
          <DetailField label="Analyzed at">
            <DateField source="analyzedAt" record={record} showTime emptyText="Not available" />
          </DetailField>
          <DetailField label="Session">{record.sessionId}</DetailField>
          <DetailField label="Job ID">{record.jobId}</DetailField>
          <DetailField label="Status">
            <StatusChip record={record} />
          </DetailField>
          <DetailField label="User">{record.session?.userEmail}</DetailField>
          <DetailField label={algorithmTimeLabel}>{formatAlgorithmProcessingTime(record)}</DetailField>
          <DetailField label={workerInstanceLabel}>{formatWorkerInstanceType(record)}</DetailField>
        </Box>
      </Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <ScoreCard
            label="Overall score"
            value={formatScore(scores?.total_score, " / 100")}
            assessment={scores?.score?.assessment?.level}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ScoreCard
            label="Gum health"
            value={formatScore(scores?.gum_health?.score, " / 100")}
            assessment={scores?.gum_health?.assessment?.level}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ScoreCard
            label="Plaque coverage"
            value={formatScore(scores?.plaque?.coverage_percent, "%")}
            assessment={scores?.plaque?.assessment?.level}
          />
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={3}>
          {hasWhitenessData ? (
            <DetailSection title="Whiteness level (zyunnbihaku)">
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
                <DetailField label="Rank">
                  {whitenessRank ? <Chip size="small" color="primary" label={whitenessRank} /> : "Not available"}
                </DetailField>
                <DetailField label="Score">{formatScore(whitenessScore)}</DetailField>
              </Box>
            </DetailSection>
          ) : null}
          {hasAvailableStainData ? (
            <MetricDetails title="Stain" scores={stainScores} score={record.stainScore} />
          ) : null}
          {hasAvailableBrightnessData ? (
            <MetricDetails title="Brightness" scores={brightnessScores} score={record.brightnessScore} />
          ) : null}
          {hasAvailableRecommendedProducts ? (
            <DetailSection title="Recommended products">
              <Grid container spacing={2}>
                {record.recommendedProducts?.length ? (
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Primary">
                      <RecommendedProducts products={record.recommendedProducts} />
                    </DetailField>
                  </Grid>
                ) : null}
                {record.recommendedAdditionalProducts?.length ? (
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Additional">
                      <RecommendedProducts products={record.recommendedAdditionalProducts} />
                    </DetailField>
                  </Grid>
                ) : null}
                {record.recommendationLogicName ? (
                  <Grid item xs={12}>
                    <DetailField label="Recommendation logic name">{record.recommendationLogicName}</DetailField>
                  </Grid>
                ) : null}
              </Grid>
            </DetailSection>
          ) : null}
        </Stack>
      </Paper>
      <AnalysisImageViewer images={{ ...record.result?.images, ...record.originalImages }} />
      {record.status === "failed" && record.errorMessage ? (
        <Paper variant="outlined" sx={{ p: 2, borderColor: "error.main" }}>
          <Typography color="error.main" fontWeight={600} gutterBottom>
            Analysis error
          </Typography>
          <Typography>{record.errorMessage}</Typography>
        </Paper>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <JsonPanel title="Session details" value={record.session} />
        </Grid>
        <Grid item xs={12} md={6}>
          <JsonPanel
            title="Analysis timing"
            value={{
              queuedAt: record.queuedAt,
              processingStartedAt: record.processingStartedAt,
              processingCompletedAt: record.processingCompletedAt,
              algorithmProcessingTimeMs: record.algorithmProcessingTimeMs,
              workerInstanceType: record.workerInstanceType,
              analyzedAt: record.analyzedAt,
              failedAt: record.failedAt,
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

const StatusChip = ({ record }: { record: AnalysisResult }) => (
  <Chip
    size="small"
    label={statusLabels[record.status] ?? record.status}
    color={statusColors[record.status] ?? "default"}
  />
);

export const MouthscanAnalysisResultsList = () => (
  <DateFilterResetProvider>
    <List
      title="Mouthscan Analysis Results"
      actions={<ListActions />}
      filters={resultFilters}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
      pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 500, 1000, 5000]} />}
    >
      <DatagridConfigurable
        preferenceKey={resultColumnsPreferenceKey}
        rowClick="show"
        bulkActionButtons={false}
        omit={["sessionId", "jobId"]}
      >
        <DateField source="createdAt" label="Created" showTime />
        <FunctionField label="Status" render={(record: AnalysisResult) => <StatusChip record={record} />} />
        <TextField source="session.userEmail" label="User" />
        <TextField source="sessionId" label="Session ID" />
        <TextField source="jobId" label="Job ID" />
        <FunctionField
          label="Overall score"
          render={(record: AnalysisResult) => formatScore(record.scores?.total_score, " / 100")}
        />
        <NumberField source="gumHealthLevel" label="Gum health" />
        <FunctionField
          label="Gum Health Label"
          render={(record: AnalysisResult) => <AssessmentChip level={record.scores?.gum_health?.assessment?.level} />}
        />
        <FunctionField
          label="Plaque coverage"
          render={(record: AnalysisResult) => formatScore(record.scores?.plaque?.coverage_percent, "%")}
        />
        <FunctionField
          label="Plaque Label"
          render={(record: AnalysisResult) => <AssessmentChip level={record.scores?.plaque?.assessment?.level} />}
        />
        <DateField source="analyzedAt" label="Completed" showTime />
        <FunctionField label={algorithmTimeLabel} sortable={false} render={formatAlgorithmProcessingTime} />
        <FunctionField label={workerInstanceLabel} sortable={false} render={formatWorkerInstanceType} />
        <WrapperField label="Actions">
          <ShowButton />
        </WrapperField>
      </DatagridConfigurable>
    </List>
  </DateFilterResetProvider>
);

export const MouthscanAnalysisResultShow = () => (
  <Show
    title="Mouthscan Analysis Result"
    actions={
      <TopToolbar>
        <ListButton label="Back to results" />
      </TopToolbar>
    }
  >
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="Overview">
        <FunctionField
          label="Result summary"
          render={(record: AnalysisResult) => <AnalysisOverview record={record} />}
        />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Analysis data">
        <FunctionField
          label="Analysis output"
          render={(record: AnalysisResult) => <JsonPanel title="Result payload" value={record.result} />}
        />
        <FunctionField
          label="Input metadata"
          render={(record: AnalysisResult) => (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <JsonPanel title="Device info" value={record.session?.deviceInfo} />
              </Grid>
              <Grid item xs={12} md={4}>
                <JsonPanel title="Questionnaire" value={record.session?.formAnswerJson} />
              </Grid>
              <Grid item xs={12} md={4}>
                <JsonPanel title="Oral health metadata" value={record.oralHealthInfo} />
              </Grid>
            </Grid>
          )}
        />
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  </Show>
);
